
import React, { useState, useEffect } from 'react';
import { Conversation, Language, AppMode } from '../../types';
import { TRANSLATIONS } from '../../constants';
import Skeleton from '../Skeleton';

interface SidebarListProps {
  conversations: Conversation[];
  currentId: string | null;
  language: Language;
  onSelect: (id: string) => void;
  onSwitchMode: (mode: AppMode) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
}

const SidebarList: React.FC<SidebarListProps> = ({ 
  conversations, currentId, language, onSelect, onSwitchMode, onClose, onDelete, onClearAll 
}) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';
  const [loading, setLoading] = useState(true);

  // Simulate initial loading for professional feel
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-1 custom-scrollbar">
      <div className="flex items-center justify-between px-3 mb-3">
        <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{t.history}</h4>
        {conversations.length > 0 && !loading && (
          <button 
            onClick={(e) => { e.stopPropagation(); onClearAll(); }}
            className="text-[9px] font-black text-rose-500 hover:bg-rose-500/10 px-2 py-1 rounded-md transition-all"
          >
            {t.clearAll}
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3 px-1">
            <div className="flex gap-3 items-center"><Skeleton className="w-8 h-8 rounded-xl" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-3/4" /><Skeleton className="h-2 w-1/2" /></div></div>
            <div className="flex gap-3 items-center"><Skeleton className="w-8 h-8 rounded-xl" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-2/3" /><Skeleton className="h-2 w-1/3" /></div></div>
            <div className="flex gap-3 items-center"><Skeleton className="w-8 h-8 rounded-xl" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-4/5" /><Skeleton className="h-2 w-1/2" /></div></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 opacity-30">
          <i className="fas fa-folder-open text-2xl mb-2 text-slate-400"></i>
          <p className="text-[10px] text-slate-400 font-bold">{isRtl ? 'السجل فارغ' : 'Empty History'}</p>
        </div>
      ) : conversations.map((conv) => (
        <div 
          key={conv.id}
          onClick={() => { onSelect(conv.id); onSwitchMode('chat'); onClose(); }}
          className={`
            group p-3 rounded-xl transition-all cursor-pointer flex items-center gap-3 border relative overflow-hidden
            ${conv.id === currentId 
              ? 'bg-emerald-500/5 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 border-emerald-500/20 shadow-sm' 
              : 'text-slate-500 border-transparent hover:bg-slate-50 dark:hover:bg-white/5'}
          `}
        >
          {conv.id === currentId && <div className={`absolute ${isRtl ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-500 rounded-full`}></div>}
          
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${conv.id === currentId ? 'bg-emerald-500/20' : 'bg-slate-100 dark:bg-white/5 group-hover:bg-white/10'}`}>
             <i className="fas fa-comment-dots text-[10px] opacity-70"></i>
          </div>
          
          <div className="flex-1 min-w-0">
             <span className="text-[11px] font-bold truncate block">{conv.title}</span>
             <span className="text-[9px] opacity-60 font-medium block">{new Date(conv.updatedAt).toLocaleDateString()}</span>
          </div>

          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }} 
            className="lg:opacity-0 lg:group-hover:opacity-100 text-slate-300 hover:text-rose-500 p-2 hover:bg-rose-500/10 rounded-lg transition-all"
          >
            <i className="fas fa-trash-can text-[10px]"></i>
          </button>
        </div>
      ))}
    </div>
  );
};
export default SidebarList;
